import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import {
  Box,
  Button,
  CommentIcon,
  Divider,
  FormOnChange,
  Icon,
  Inline,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@procsea/design-system'

import { FormattedOrderPermissions } from 'src/types'

export interface CommentsFormValue {
  customerComment: string
  deliveryNoteComment: string
  orderNoteComment: string
}

const StyledBox = styled(Box)`
  width: 250px;
`

interface CommentFormProps {
  'data-e2e'?: string
  defaultValues: CommentsFormValue
  onSubmit: (formValue: CommentsFormValue) => void
  orderPermissions?: FormattedOrderPermissions
}

const CommentForm = ({
  'data-e2e': dataE2e,
  defaultValues,
  onSubmit,
  orderPermissions,
}: CommentFormProps) => {
  const form = useForm<CommentsFormValue>({ defaultValues })
  const fields = useWatch<CommentsFormValue>({ control: form.control })

  useEffect(() => {
    if (orderPermissions?.attributes.comment.enabled) form.register('customerComment')

    form.register('deliveryNoteComment')
    form.register('orderNoteComment')
  }, [])

  const handleChangeCustomerComment: FormOnChange<string> = useCallback(({ value }) => {
    form.setValue('customerComment', value)
  }, [])

  const handleChangeDeliveryNoteComment: FormOnChange<string> = useCallback(({ value }) => {
    form.setValue('deliveryNoteComment', value)
  }, [])

  const handleChangeOrderNoteComment: FormOnChange<string> = useCallback(({ value }) => {
    form.setValue('orderNoteComment', value)
  }, [])

  const handleSubmit: React.FormEventHandler<HTMLElement> = event => {
    event.stopPropagation()
    form.handleSubmit(onSubmit)(event)
  }

  return (
    <StyledBox inset="small">
      <form data-e2e={dataE2e} onSubmit={handleSubmit} noValidate>
        <Stack spacing="small">
          <Text bold data-e2e="comment-title" size="large" variant="secondary">
            {gettext('Add a comment')}
          </Text>

          <Divider />

          <Textarea
            data-e2e="comment-field-customer"
            disabled={!orderPermissions?.attributes.comment.enabled}
            fluid
            helpText={orderPermissions?.attributes.comment.disabledReason}
            label={`${gettext('Order sheet')} (${gettext('visible by customer')})`}
            onChange={handleChangeCustomerComment}
            rows={2}
            value={fields.customerComment}
          />

          <Textarea
            data-e2e="comment-field-order-note"
            disabled={!orderPermissions?.attributes.providerInternalCommentOrderNote.enabled}
            fluid
            helpText={orderPermissions?.attributes.providerInternalCommentOrderNote.disabledReason}
            label={`${gettext('Order sheet')} (${gettext('internal')})`}
            onChange={handleChangeOrderNoteComment}
            rows={2}
            value={fields.orderNoteComment}
          />

          <Textarea
            data-e2e="comment-field-delivery-note"
            disabled={!orderPermissions?.attributes.providerCommentDeliveryNote.enabled}
            fluid
            helpText={orderPermissions?.attributes.providerCommentDeliveryNote.disabledReason}
            label={`${gettext('Delivery note')} (${gettext('visible by customer')})`}
            onChange={handleChangeDeliveryNoteComment}
            rows={2}
            value={fields.deliveryNoteComment}
          />

          <Button data-e2e="comment-form-submit-button" type="submit" variant="success">
            {gettext('Validate')}
          </Button>
        </Stack>
      </form>
    </StyledBox>
  )
}

export interface CommentFormTooltipProps {
  'data-e2e'?: string
  customerComment?: string | null
  deliveryNoteComment?: string | null
  format?: 'button' | 'icon'
  onCommentsSubmit: (formValue: CommentsFormValue) => void | Promise<void>
  orderNoteComment?: string | null
  orderPermissions?: FormattedOrderPermissions
}

const CommentFormTooltip = ({
  'data-e2e': dataE2e = 'comment-form',
  customerComment,
  deliveryNoteComment,
  format = 'icon',
  onCommentsSubmit,
  orderNoteComment,
  orderPermissions,
}: CommentFormTooltipProps) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  const handleSubmit = useCallback(
    (formValue: CommentsFormValue) => {
      setTimeout(() => {
        onCommentsSubmit(formValue)
      }, 0)
      setIsTooltipOpen(false)
    },
    [onCommentsSubmit]
  )

  const hasComment = !!customerComment || !!orderNoteComment || !!deliveryNoteComment

  const defaultValues: CommentsFormValue = useMemo(
    () => ({
      customerComment: customerComment ?? '',
      deliveryNoteComment: deliveryNoteComment ?? '',
      orderNoteComment: orderNoteComment ?? '',
    }),
    [customerComment, deliveryNoteComment, orderNoteComment]
  )

  return (
    <Tooltip
      data-e2e="comment-form-tooltip"
      onVisibilityChange={trigger => setIsTooltipOpen(trigger)}
      open={isTooltipOpen}
      tooltip={
        <CommentForm
          defaultValues={defaultValues}
          data-e2e={dataE2e}
          onSubmit={handleSubmit}
          orderPermissions={orderPermissions}
        />
      }
      trigger="click"
      withPortal
    >
      {format === 'icon' && (
        <CommentIcon data-e2e="order-item-comment" hasDot={hasComment} hasPlus={!hasComment} />
      )}

      {format === 'button' && (
        <Button data-e2e="comment-tooltip-button" outline variant="primary" hasInputHeight>
          <Inline spacing="xsmall" alignment="center">
            <Icon
              data-e2e={hasComment ? 'button-has-comment' : 'button-has-no-comment'}
              faStyle="far"
              hasDot={hasComment}
              name="comment"
              variant="primary"
            />
            <Text bold>{gettext('Comment')}</Text>
          </Inline>
        </Button>
      )}
    </Tooltip>
  )
}

export default memo(CommentFormTooltip)
